import type { UserProfile } from '@types';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { ProfileModal } from '@/components/modals/ProfileModal';
import { Tooltip } from '@/components/common';
import { useSeiBalance } from '@hooks';
import {
  generateAvatarFromAddress,
  generateNameFromAddress,
} from '@/shared/utils';
import { useMusic } from '@/contexts/MusicContext';

// Import header assets
import goldIcon from '@/assets/header/gold.webp';
import seiIcon from '@/assets/header/sei.webp';
import energyIcon from '@/assets/header/energy.webp';
import pointIcon from '@/assets/header/point.webp';
import plusIcon from '@/assets/header/plus.webp';
import moreInfoIcon from '@/assets/header/more-info.webp';

export interface HeaderStatusBarProps {
  user: UserProfile;
  isFirstMount?: boolean;
  onShopClick?: () => void;
  onModalStateChange?: (isOpen: boolean) => void;
}

export const HeaderStatusBar = ({
  user,
  isFirstMount = false,
  onShopClick,
  onModalStateChange,
}: HeaderStatusBarProps) => {
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const handleOpenProfileModal = () => {
    setIsProfileModalOpen(true);
    onModalStateChange?.(true);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
    onModalStateChange?.(false);
  };
  const { balance: seiBalance } = useSeiBalance();
  const { isMuted, isPlaying, toggleMute } = useMusic();

  return (
    <motion.div
      className="fixed top-0 left-0 right-0 flex flex-col items-end px-5 pt-7 w-full z-50 max-w-[var(--app-max-width)] mx-auto gap-4"
      initial={isFirstMount ? { opacity: 0 } : { opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        isFirstMount
          ? { duration: 0.3, ease: 'easeOut', delay: 0.6 }
          : { duration: 0.5, ease: 'easeOut', delay: 0.4 }
      }
    >
      {/* Resource Container */}
      <motion.div
        className="flex items-center w-full gap-3 px-1"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: isFirstMount
              ? {
                  staggerChildren: 0,
                  delayChildren: 0.7,
                }
              : {
                  staggerChildren: 0.1,
                  delayChildren: 0.5,
                },
          },
        }}
      >
        {/* Gold Resource Group */}
        <Tooltip content="Gold - Used to upgrade defenders!" side="bottom">
          <motion.div
            className="relative flex items-center justify-center w-full gap-2 bg-black/30 h-[22px] pr-1 backdrop-blur-[2px] cursor-pointer"
            variants={
              isFirstMount
                ? {
                    hidden: { opacity: 0 },
                    visible: { opacity: 1 },
                  }
                : {
                    hidden: { scale: 0.8, opacity: 0 },
                    visible: { scale: 1, opacity: 1 },
                  }
            }
            whileHover={{
              scale: 1.02,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              transition: { duration: 0.2 },
            }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.img
              src={goldIcon}
              alt="Gold"
              className="absolute w-7 h-7 z-[2] -left-1 scale-110"
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            <span className="font-lilita text-sm leading-4 text-white [text-shadow:0.5px_0.5px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
              {user.gold_balance.toLocaleString()}
            </span>
            <motion.img
              src={plusIcon}
              alt="Add"
              className="absolute w-7 h-7 z-[1] right-0 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onShopClick?.();
              }}
              whileHover={{ scale: 1.2, rotate: 360 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.2 }}
            />
          </motion.div>
        </Tooltip>

        {/* SEI Resource Group */}
        <Tooltip content="SEI Token - Premium currency!" side="bottom">
          <motion.div
            className="relative flex items-center justify-center w-full gap-2 bg-black/30 h-[22px] ml-1 pr-1 backdrop-blur-[2px] cursor-pointer"
            variants={
              isFirstMount
                ? {
                    hidden: { opacity: 0 },
                    visible: { opacity: 1 },
                  }
                : {
                    hidden: { scale: 0.8, opacity: 0 },
                    visible: { scale: 1, opacity: 1 },
                  }
            }
            whileHover={{
              scale: 1.02,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              transition: { duration: 0.2 },
            }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.img
              src={seiIcon}
              alt="SEI"
              className="absolute w-7 h-7 z-[2] -left-[6px] scale-110"
              transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.3,
              }}
            />
            <span className="font-lilita text-sm leading-4 text-white [text-shadow:0.5px_0.5px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
              {seiBalance.toLocaleString()}
            </span>
            <motion.img
              src={moreInfoIcon}
              alt="More Info"
              className="absolute w-7 h-7 z-[1] right-0 cursor-pointer"
              whileHover={{ scale: 1.2, rotate: 360 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.2 }}
            />
          </motion.div>
        </Tooltip>

        {/* Energy Resource Group */}
        <Tooltip
          content="Energy - For battles. Regens over time!"
          side="bottom"
        >
          <motion.div
            className="relative flex items-center justify-center w-full gap-2 bg-black/30 h-[22px] ml-1 pr-1 backdrop-blur-[2px] cursor-pointer"
            variants={
              isFirstMount
                ? {
                    hidden: { opacity: 0 },
                    visible: { opacity: 1 },
                  }
                : {
                    hidden: { scale: 0.8, opacity: 0 },
                    visible: { scale: 1, opacity: 1 },
                  }
            }
            whileHover={{
              scale: 1.02,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              transition: { duration: 0.2 },
            }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.img
              src={energyIcon}
              alt="Energy"
              className="absolute w-7 h-7 z-[2] -left-2 top-0 scale-125"
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.6,
              }}
            />
            <span className="font-lilita text-sm leading-4 text-white [text-shadow:0.5px_0.5px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
              {user.current_energy.toLocaleString()}/20
            </span>
            <motion.img
              src={moreInfoIcon}
              alt="More Info"
              className="absolute w-7 h-7 z-[1] right-0 cursor-pointer"
              whileHover={{ scale: 1.2, rotate: 360 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.2 }}
            />
          </motion.div>
        </Tooltip>
      </motion.div>

      {/* Player Info Container */}
      <motion.div
        className="flex justify-between items-center self-stretch bg-black/30 backdrop-blur-[2px] p-1.5 gap-4 rounded-[10px]"
        initial={isFirstMount ? { opacity: 0 } : { opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={
          isFirstMount
            ? { delay: 0.8, duration: 0.3, ease: 'easeOut' }
            : { delay: 0.8, duration: 0.5, ease: 'easeOut' }
        }
      >
        {/* Left: Avatar + Name + Logout */}
        <div className="flex items-center gap-1.5">
          {user.address && (
            <Tooltip content="View Profile" side="bottom" disableOnMobile>
              <motion.img
                src={generateAvatarFromAddress(user.address, 44)}
                alt={generateNameFromAddress(user.address)}
                className="w-9 h-9 border border-black rounded-md cursor-pointer"
                onClick={handleOpenProfileModal}
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
              />
            </Tooltip>
          )}
          <div className="flex items-center gap-2">
            <Tooltip content="View Profile" side="bottom" disableOnMobile>
              <motion.div
                className="flex flex-col gap-0.5 max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                onClick={handleOpenProfileModal}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="font-lilita text-lg leading-[21px] text-white overflow-hidden text-ellipsis whitespace-nowrap [text-shadow:0.5px_0.5px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
                  {generateNameFromAddress(user.address)}
                </span>
              </motion.div>
            </Tooltip>

            {/* Music Control Button */}
            <Tooltip
              content={
                isMuted || !isPlaying ? 'Click to play music' : 'Music playing'
              }
              side="bottom"
            >
              <motion.button
                onClick={toggleMute}
                className="flex items-center justify-center cursor-pointer"
                whileHover={{
                  scale: 1.15,
                }}
                whileTap={{ scale: 0.9 }}
                transition={{ duration: 0.2 }}
                aria-label={
                  isMuted || !isPlaying ? 'Unmute music' : 'Mute music'
                }
              >
                {isMuted || !isPlaying ? (
                  <svg
                    className="w-6 h-6 text-white relative z-[1]"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    style={{
                      filter: 'drop-shadow(0.5px 0.5px 0px #000)',
                    }}
                  >
                    <path d="M3.63 3.63a.996.996 0 000 1.41L7.29 8.7 7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91-.36.15-.58.53-.58.92 0 .72.73 1.18 1.39.91.8-.33 1.55-.77 2.22-1.31l1.34 1.34a.996.996 0 101.41-1.41L5.05 3.63c-.39-.39-1.02-.39-1.42 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85C17.18 6.54 19 9.06 19 12zm-8.71-6.29l-.17.17L12 7.76V6.41c0-.89-1.08-1.33-1.71-.7zM16.5 12A4.5 4.5 0 0014 7.97v1.79l2.48 2.48c.01-.08.02-.16.02-.24z" />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 text-white relative z-[1]"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    style={{
                      filter: 'drop-shadow(0.5px 0.5px 0px #000)',
                    }}
                  >
                    <path d="M3 10v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71V6.41c0-.89-1.08-1.34-1.71-.71L7 9H4c-.55 0-1 .45-1 1zm13.5 2A4.5 4.5 0 0014 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 4.45v.2c0 .38.25.71.6.85C17.18 6.53 19 9.06 19 12s-1.82 5.47-4.4 6.5c-.36.14-.6.47-.6.85v.2c0 .63.63 1.07 1.21.85C18.6 19.11 21 15.84 21 12s-2.4-7.11-5.79-8.4c-.58-.23-1.21.22-1.21.85z" />
                  </svg>
                )}
              </motion.button>
            </Tooltip>
          </div>
        </div>

        {/* Right: Score */}
        <Tooltip content="Score - Used for leaderboard ranking!" side="bottom">
          <motion.div
            className="flex justify-end items-center relative isolate w-20 h-[22px] cursor-pointer"
            initial="rest"
            whileHover="hover"
            whileTap={{ scale: 0.98 }}
            variants={{
              rest: { scale: 1 },
              hover: { scale: 1.05 },
            }}
          >
            <motion.img
              src={pointIcon}
              alt="Score"
              className="absolute w-8 h-8 z-[1] -left-[15px] top-1/2 -translate-y-1/2"
              variants={{
                rest: { rotate: 0 },
                hover: { rotate: 360 },
              }}
              transition={{
                duration: 0.4,
                ease: 'easeInOut',
              }}
            />
            <div className="flex justify-end items-center flex-1 bg-black/30 z-0 py-1.5 pl-3 pr-2 gap-1 rounded">
              <span className="font-lilita text-sm leading-4 text-white [text-shadow:0.5px_0.5px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
                {user.scores.toLocaleString()}
              </span>
            </div>
          </motion.div>
        </Tooltip>
      </motion.div>

      {/* Profile Modal */}
      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={handleCloseProfileModal}
        user={user}
        onLogoutModalChange={onModalStateChange}
      />
    </motion.div>
  );
};
